import { Result, failure, success } from '../shared'
import { SecurityProvider } from './security.provider'
import { FirewallProvider } from './firewall.provider'
import { IpAddressProvider } from './ipAddress.provider'
import {
  SecurityServiceErrorCode,
  SecurityUsersServerData,
  SecurityServiceFailure,
  SecurityGroupsServerData,
  SecurityIPAccessesServerData,
  FirewallEntriesServerData,
  FirewallRule,
  AddIpAddressEntry,
  EditIpAddressEntry,
  DeleteIpAddressEntry,
} from './security.types'
import { SecurityAction } from './security.constants'

export class SecurityService {
  constructor(
    private securityProvider: SecurityProvider,
    private firewallProvider: FirewallProvider,
    private ipAddressProvider: IpAddressProvider,
  ) {}

  public async users(): Promise<Result<SecurityUsersServerData, SecurityServiceFailure>> {
    try {
      const { data } = await this.securityProvider.get<SecurityUsersServerData>(SecurityAction.Users)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async groups(): Promise<Result<SecurityGroupsServerData, SecurityServiceFailure>> {
    try {
      const { data } = await this.securityProvider.get<SecurityGroupsServerData>(SecurityAction.Groups)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async ipAccesses(): Promise<Result<SecurityIPAccessesServerData, SecurityServiceFailure>> {
    try {
      const { data } = await this.securityProvider.get<SecurityIPAccessesServerData>(SecurityAction.IPAccesses)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addIpAddress(params: AddIpAddressEntry): Promise<any> {
    try {
      const { data } = await this.ipAddressProvider.add(params)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async editIpAddress(params: EditIpAddressEntry): Promise<any> {
    try {
      const { data } = await this.ipAddressProvider.edit(params)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteIpAddress(params: DeleteIpAddressEntry): Promise<any> {
    try {
      const { data } = await this.ipAddressProvider.delete(params)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async firewalls(rule: FirewallRule): Promise<Result<FirewallEntriesServerData>> {
    try {
      const { data } = await this.firewallProvider.get(rule)
      return success(data)
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL)
    }
  }

  public async addFirewall(
    ip: string,
    mask: string,
    rule: FirewallRule,
    expire: number,
    comment: string,
  ): Promise<Result<void>> {
    try {
      await this.firewallProvider.add(ip, mask, rule, expire, comment)
      return success()
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL)
    }
  }

  public async deleteFirewall(ip: string, rule: FirewallRule): Promise<Result<void>> {
    try {
      await this.firewallProvider.delete(ip, rule)
      return success()
    } catch (error) {
      return failure(SecurityServiceErrorCode.GENERAL)
    }
  }
}
